
import { useState, useMemo } from 'react';
import { Transaction } from '@/types/payment';

export function useTransactionFiltering(transactions: Transaction[]) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<keyof Transaction>('timestamp');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Handle search
  const filteredTransactions = useMemo(() => {
    return transactions.filter(tx => {
      const searchLower = searchQuery.toLowerCase();
      return (
        tx.id.toLowerCase().includes(searchLower) ||
        tx.sender.toLowerCase().includes(searchLower) ||
        tx.senderEmail.toLowerCase().includes(searchLower) ||
        tx.recipient.toLowerCase().includes(searchLower) ||
        tx.recipientEmail.toLowerCase().includes(searchLower) ||
        tx.status.toLowerCase().includes(searchLower) ||
        tx.method.toLowerCase().includes(searchLower)
      );
    });
  }, [transactions, searchQuery]);

  // Handle sorting
  const sortedTransactions = useMemo(() => {
    return [...filteredTransactions].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue) 
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' 
          ? aValue - bValue 
          : bValue - aValue;
      }
      
      return 0;
    });
  }, [filteredTransactions, sortField, sortDirection]);

  // Handle sort toggling
  const toggleSort = (field: keyof Transaction) => {
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return {
    searchQuery,
    setSearchQuery,
    sortField,
    sortDirection,
    toggleSort,
    sortedTransactions
  };
}
